import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";

export type EventFileEntry = {
  guid: string;
  farmEventGuid: string;
  creationDate: string;
  fileEntryGuid: string;
  fileDescription: string;
  fileUploadType: string;
  fileName: string;
  fileEntryFileUploadGuid: string;
  uploaderGuid: string;
};

const baseUrl = `${import.meta.env.VITE_API_ENDPOINT}/v2/eventFileEntry`;

function authHeaders() {
  return {
    Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
    "Content-Type": "application/json",
  };
}

async function fetchEventFiles(
  farmEventGuid: string
): Promise<EventFileEntry[]> {
  const res = await fetch(
    `${baseUrl}?farmEventGuid=${encodeURIComponent(farmEventGuid)}`,
    { headers: authHeaders() }
  );
  if (!res.ok) return [];
  return (await res.json()) as EventFileEntry[];
}

async function fetchEventFilesByEvents(
  farmEventGuids: string[],
  actionSubmissionGuid?: string
): Promise<EventFileEntry[]> {
  if (farmEventGuids.length === 0) return [];
  let url = `${baseUrl}/byEvents?farmEventGuids=${encodeURIComponent(
    farmEventGuids.join(",")
  )}`;
  if (actionSubmissionGuid) {
    url += `&actionSubmissionGuid=${encodeURIComponent(actionSubmissionGuid)}`;
  }
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) return [];
  return (await res.json()) as EventFileEntry[];
}

async function fetchEventFileCounts(
  farmEventGuids: string[]
): Promise<Record<string, number>> {
  if (farmEventGuids.length === 0) return {};
  const res = await fetch(
    `${baseUrl}/counts?farmEventGuids=${encodeURIComponent(
      farmEventGuids.join(",")
    )}`,
    { headers: authHeaders() }
  );
  if (!res.ok) return {};
  return (await res.json()) as Record<string, number>;
}

export function useEventFiles(farmEventGuid: string | undefined) {
  return useQuery({
    queryKey: queryKeys.eventFiles.byEvent(farmEventGuid ?? ""),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: () => fetchEventFiles(farmEventGuid!),
    enabled: !!farmEventGuid,
  });
}

export function useEventFileCounts(farmEventGuids: string[]) {
  return useQuery({
    queryKey: queryKeys.eventFiles.counts(farmEventGuids),
    queryFn: () => fetchEventFileCounts(farmEventGuids),
    enabled: farmEventGuids.length > 0,
  });
}

export function useEventFilesByEvents(
  farmEventGuids: string[],
  actionSubmissionGuid?: string
) {
  return useQuery({
    queryKey: queryKeys.eventFiles.byEvents(
      farmEventGuids,
      actionSubmissionGuid
    ),
    queryFn: () =>
      fetchEventFilesByEvents(farmEventGuids, actionSubmissionGuid),
    enabled: farmEventGuids.length > 0,
  });
}

export function useCreateEventFileEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      fileEntryGuid,
      farmEventGuid,
    }: {
      fileEntryGuid: string;
      farmEventGuid: string;
    }) => {
      const res = await fetch(baseUrl, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ fileEntryGuid, farmEventGuid }),
      });
      if (!res.ok) throw new Error("Failed to link file to event");
      return (await res.json()) as { guid: string };
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.eventFiles.byEvent(variables.farmEventGuid),
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.eventFiles.all,
      });
    },
  });
}

export function useDeleteEventFileEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      guid,
      farmEventGuid,
    }: {
      guid: string;
      farmEventGuid: string;
    }) => {
      const res = await fetch(`${baseUrl}/${guid}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error("Failed to remove file from event");
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.eventFiles.byEvent(variables.farmEventGuid),
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.eventFiles.all,
      });
    },
  });
}
